import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  HStack,
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseTrigger,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalPositioner,
  ModalTitle,
  Stack,
  Text,
  Textarea,
} from "@commonsku/design-system";

const feedbackReasons = [
  "Incorrect or Incomplete",
  "Not what I asked for",
  "Slow or buggy",
  "Poor style or tone",
  "Other",
];

export interface SubmitFeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (comment: string) => void;
}

export function SubmitFeedbackDialog(props: SubmitFeedbackDialogProps) {
  const { open, onOpenChange, onSubmit } = props;

  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [comment, setComment] = useState("");

  useEffect(() => {
    if (open) {
      setSelectedReason(null);
      setComment("");
    }
  }, [open]);

  function close() {
    setSelectedReason(null);
    setComment("");
    onOpenChange(false);
  }

  function handleSubmit() {
    const details = comment.trim();
    const submittedComment =
      selectedReason && selectedReason !== "Other"
        ? `${selectedReason}: ${details}`.trim()
        : details;
    onSubmit(submittedComment);
    setSelectedReason(null);
    setComment("");
    onOpenChange(false);
  }

  return (
    <Modal
      open={open}
      onOpenChange={(details: { open: boolean }) => {
        if (!details.open) {
          close();
        }
      }}
      lazyMount
    >
      <ModalBackdrop bg="rgba(15, 23, 42, 0.35)" />
      <ModalPositioner>
        <ModalContent
          w="min(430px, calc(100vw - 32px))"
          mx="4"
          borderRadius="md"
          boxShadow="0 18px 45px rgba(15, 23, 42, 0.22)"
        >
          <ModalHeader px="6" pb={0} mb={0}>
            <ModalTitle fontSize="md" fontWeight="semibold" fontFamily="heading" margin="0" pb="0">
              Submit feedback
            </ModalTitle>
            <ModalCloseTrigger>✕</ModalCloseTrigger>
          </ModalHeader>
          <ModalBody px="6" pb="4">
            <Stack gap="4">
                <HStack gap="2" flexWrap="wrap">
                  {feedbackReasons.map((reason) => {
                    const selected = selectedReason === reason;

                    return (
                      <ReasonButton
                        key={reason}
                        selected={selected}
                        onClick={() => setSelectedReason((current) => current === reason ? null : reason)}
                      >
                        {reason}
                      </ReasonButton>
                    );
                  })}
                </HStack>

                <Box>
                  <Text mb="1.5" fontSize="sm" fontWeight="medium" mt="0" mx="0">
                    Feedback details
                  </Text>

                  <CommentTextarea
                    value={comment}
                    onChange={(value) => setComment(value)}
                  />
                </Box>

                <Box bg="gray.50" borderRadius="md" px="3" py="3">
                  <Text fontSize="xs" color="gray.700" lineHeight="1.4" margin="0">
                    Your conversation will be included with your feedback to help
                    improve skubot.
                  </Text>
                </Box>
              </Stack>
          </ModalBody>
          <ModalFooter px="6" pt="2" pb="4" gap="2" justifyContent="flex-end">
            <Button
              variant="outline"
              bg={"white"}
              boxShadow="none"
              outline="none"
              borderWidth="1px"
              borderStyle="solid"
              borderColor="border.muted"
              backgroundColor="white"
              color="gray.800"
              _hover={{
                bg: "gray.50",
                borderColor: "border.subtle",
              }}
              onClick={close}>
              Cancel
            </Button>

            <Button
              bg="brand.fg"
              color="white"
              _hover={{ bg: "brand.fg", opacity: 0.9 }}
              onClick={handleSubmit}
            >
              Submit
            </Button>
          </ModalFooter>
        </ModalContent>
      </ModalPositioner>
    </Modal>
  );
}


function ReasonButton({ children, selected, onClick }: { children: React.ReactNode, selected: boolean, onClick: () => void }) {
  return (
    <Button
      type="button"
      aria-pressed={selected ? "true" : "false"}
      size="sm"
      h="36px"
      px="3.5"
      borderRadius="full"
      variant="outline"
      fontWeight="normal"
      fontSize="sm"
      fontFamily="body"
      bg={selected ? "gray.100" : "white"}
      boxShadow="none"
      outline="none"
      borderWidth="1px"
      borderStyle="solid"
      borderColor={selected ? "cyan.subtle" : "border.muted"}
      backgroundColor={selected ? "cyan.subtle" : "white"}
      color={selected ? "cyan.800" : "gray.800"}
      _hover={{
        bg: selected ? "cyan.subtle" : "gray.50",
        borderColor: selected ? "cyan.subtle" : "border.muted",
      }}
      onClick={onClick}
    >{children}</Button>
  );
};


function CommentTextarea({ value, onChange, placeholder = "Share details here..." }: { value: string, onChange: (value: string) => void, placeholder?: string }) {
  return (
    <Textarea
      aria-label="Feedback details"
      value={value}
      minH="96px"
      resize="vertical"
      placeholder={placeholder}
      fontSize="sm"
      boxShadow="none"
      outline="none"
      borderWidth="1px"
      borderStyle="solid"
      borderColor="border.muted"
      bg="white"
      color="gray.800"
      _placeholder={{ color: "gray.400" }}
      _focus={{
        borderColor: "brand.focusRing",
        boxShadow: `0 0 0 2px var(--chakra-colors-brand-subtle)`,
      }}
      _focusVisible={{
        borderColor: "brand.focusRing",
        boxShadow: `0 0 0 2px var(--chakra-colors-brand-subtle)`,
        outline: "none",
      }}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}